import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { TypedMemo } from '../../../common/types';
import type { ReleaseOption } from '../hooks/useReleaseOptions';
import { shortDate } from '../outreachFormat';
import { songTiming } from './builderFormat';
import { campaignMessages as messages } from './campaignMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for Outreach; each part uses its own slice of it */
import shared from '../Outreach.module.scss';
import styles from './Builder.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SongTilesProps {
  options: ReleaseOption[];
  value: string | null;
  onChange: (catalogItemUuid: string) => void;
  /** Plug pitched the song: the other songs cannot be picked. */
  locked: boolean;
}

/** The song tiles of question 1 (design outreach-v2 b1), in pitch order (`useReleaseOptions`). */
const SongTilesComponent: React.FC<SongTilesProps> = ({ options, value, onChange, locked }) => {
  const intl = useIntl();
  const tiles = useMemo(() => {
    const now = new Date();
    return options.map((option) => {
      const on = option.catalogItemUuid === value;
      const timing = songTiming(option.releaseDate, now);
      return (
        <button
          key={option.catalogItemUuid}
          type="button"
          className={clsx(styles.pickable, styles.song, on && styles.picked, on && styles.songPicked)}
          onClick={() => onChange(option.catalogItemUuid)}
          aria-pressed={on}
          disabled={locked && !on}
          data-testid="builder-song"
        >
          {option.coverUrl !== null ? (
            <img className={styles.songCover} src={option.coverUrl} alt="" />
          ) : (
            <span className={styles.songCover} />
          )}
          <span className="text-truncate">
            <b className="text-truncate">{option.title}</b>
            <small>
              {option.releaseDate === null
                ? intl.formatMessage(messages.songNoDate)
                : intl.formatMessage(messages.songOut, { date: shortDate(intl, option.releaseDate) })}
            </small>
          </span>
          {timing !== null && (
            <span className={clsx(shared.chip, timing === 'best' && shared.chipVio, styles.songStat)}>
              {intl.formatMessage(timing === 'best' ? messages.songBestTime : messages.songOlder)}
            </span>
          )}
        </button>
      );
    });
  }, [intl, locked, onChange, options, value]);
  return <div className={styles.songs}>{tiles}</div>;
};

export const SongTiles = TypedMemo(SongTilesComponent);
